import type { Mp3FormatInfo } from './types'

const BITRATE_TABLE: Record<string, number[]> = {
  // MPEG 1, Layer III
  '1-1': [0, 32, 40, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320, -1],
  // MPEG 2 / 2.5, Layer III
  '2-1': [0, 8, 16, 24, 32, 40, 48, 56, 64, 80, 96, 112, 128, 144, 160, -1],
}

const SAMPLE_RATE_TABLE: Record<string, number[]> = {
  '1': [44100, 48000, 32000],
  '2': [22050, 24000, 16000],
  '2.5': [11025, 12000, 8000],
}

export function id3v2Length(buf: Uint8Array): number {
  if (buf.length < 10) return 0
  if (buf[0] !== 0x49 || buf[1] !== 0x44 || buf[2] !== 0x33) return 0
  const size =
    ((buf[6] & 0x7f) << 21) |
    ((buf[7] & 0x7f) << 14) |
    ((buf[8] & 0x7f) << 7) |
    (buf[9] & 0x7f)
  return 10 + size
}

export function sniffMp3Format(buf: Uint8Array): Mp3FormatInfo | null {
  const start = id3v2Length(buf)
  const end = Math.min(buf.length - 4, start + 8192)
  for (let i = start; i < end; i++) {
    if (buf[i] !== 0xff) continue
    const b1 = buf[i + 1]
    if ((b1 & 0xe0) !== 0xe0) continue

    const versionBits = (b1 >> 3) & 0x03
    const layerBits = (b1 >> 1) & 0x03
    if (versionBits === 1) continue
    if (layerBits !== 1) continue

    const version = versionBits === 3 ? '1' : versionBits === 2 ? '2' : '2.5'
    const bitrateGroup = version === '1' ? '1-1' : '2-1'

    const b2 = buf[i + 2]
    const bitrateIndex = (b2 >> 4) & 0x0f
    const sampleRateIndex = (b2 >> 2) & 0x03
    if (bitrateIndex === 0 || bitrateIndex === 15) continue
    if (sampleRateIndex === 3) continue

    const kbps = BITRATE_TABLE[bitrateGroup][bitrateIndex]
    const sampleRate = SAMPLE_RATE_TABLE[version][sampleRateIndex]

    const b3 = buf[i + 3]
    const channelMode = (b3 >> 6) & 0x03
    const channels: 1 | 2 = channelMode === 3 ? 1 : 2

    return { channels, sampleRate, kbps }
  }
  return null
}

export function isConformant(info: Mp3FormatInfo): boolean {
  return info.channels === 1 && info.sampleRate === 44100 && info.kbps <= 128
}
